import { ApiError } from '@nyce/admin-kit/api';
import { Chip } from '@nyce/admin-kit/ui';
import { useEditorForm as useKitEditorForm } from '@nyce/admin-kit/console';
import { Tabs } from './common';
import type { ClusterEnvironment } from '../api';

// The registry pages' form pieces; the generic ones (id field, editor dialog, slugs, switch field) are the kit's.
export { EditorDialog, IdField, SLUG, slugify, SwitchField } from '@nyce/admin-kit/console';

/** Kubernetes namespace / GKE cluster name: DNS label (same rule as the API). */
export const DNS_LABEL = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
export const isDnsLabel = (s: string) => s.length <= 63 && DNS_LABEL.test(s);

/** Server errors in the words an admin needs; `409 in_use` names what blocks the delete. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError && e.status === 409 && e.code === 'in_use')
    return 'Still in use by an environment, copy config or schedule';
  return (e as Error)?.message || String(e);
}

export const byName = <T extends { name: string; id: string }>(a: T, b: T) =>
  (a.name || a.id).localeCompare(b.name || b.id, undefined, { sensitivity: 'base' });

export const ENV_OPTIONS: { value: ClusterEnvironment; label: string }[] = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Staging' },
];

/** Production / staging (and the environment kinds) as a coloured chip. */
export function EnvChip({ env }: { env: string }) {
  return (
    <Chip size="sm" className={`oph-env oph-env-${env}`}>
      {env}
    </Chip>
  );
}

/** Segmented production/staging picker with a field label. */
export function EnvField({
  value,
  onChange,
  label = 'Environment',
}: {
  value: ClusterEnvironment;
  onChange: (v: ClusterEnvironment) => void;
  label?: string;
}) {
  return (
    <div className="nyk-ui-field oph-env-field">
      <span>{label}</span>
      <Tabs value={value} onChange={onChange} options={ENV_OPTIONS} />
    </div>
  );
}

/** Create/edit dialog form state; the server error in the words above (`errorText`). */
export function useEditorForm<T extends { name: string }>(initial: T & { id: string }, isNew: boolean) {
  return useKitEditorForm(initial, isNew, errorText);
}
